import React, { useState, useEffect } from "react";
import axios from "axios";
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import moment from "moment";
import { SummaryActuality } from "helpers/Enums";

interface ISummariesContext {
    recordCount: number
    search: ISearchRequest
    summaries: ISummary[]
    isLoading: boolean

    doSearch: (search: ISearchRequest) => void
    handleChangePage: (event: any, page: number) => void
    searchSummaries: (searchRequest: ISearchRequest) => Promise<void>
    updateSearch: (request: ISearchRequest) => void
    searchCustomers: (searchText: string, etag: number) => Promise<any>
}

export interface ISummary {
    customerId: number
    customerName: string
    username: string
    userDisplayName: string
    ticketNumber: string
    ticketTitle: string
    countryCode: string
    countryName: string
    startDate: moment.Moment
    endDate: moment.Moment
    actuality: SummaryActuality
}

interface ISearchRequest {
    customerId?: number
    customerName: string
    technicians: string[]
    dateFrom: moment.Moment
    dateTo: moment.Moment
    countryCode: string
    
    orderByCustomer: boolean

    pageNr: number
    pageSize: number
}

export const defaultSearch: ISearchRequest = {
    customerId: 0,
        customerName: '',
        technicians: [],
        dateFrom: moment().add(-7, 'days').startOf('day').utc(true),
        dateTo: moment().add(+7, 'days').startOf('day').utc(true),
        countryCode: '',
        orderByCustomer: true,
        pageNr: 0,
        pageSize: 20
}

export let customersSummaryContext = React.createContext({} as ISummariesContext);

let { Provider } = customersSummaryContext;

let CustomersSummaryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let history = useHistory();
    let location = useLocation();

    let [isLoading, setLoading] = useState<boolean>(true);

    let [summaries, setSummaries] = useState<ISummary[]>([]);
    let [recordCount, setTotalRecords] = useState(0);

    let [search, setSearch] = useState<ISearchRequest>(defaultSearch);

    useEffect(() => {
        if (location.state)
            processState(location.state);
    }, [location.state])

    if (!location.state)
        return <Redirect to={{ pathname: location.pathname, state: {
            search: {
                ...search,
                dateFrom: search.dateFrom != null ? moment(search.dateFrom).toDate() : null,
                dateTo: search.dateTo != null ? moment(search.dateTo).toDate() : null
            }
        } }} />

    let processState = (state: any) => {
        if (state.search) {
            let searchRequest = {
                ...state.search,
                dateFrom: state.search?.dateFrom != null ? moment(state.search.dateFrom).utc(true) : null,
                dateTo: state.search?.dateTo != null ? moment(state.search.dateTo).utc(true) : null
            }
            setSearch(searchRequest);
            searchSummaries(searchRequest);
        }
    }

    let doSearch = (values: ISearchRequest) => {
        let request = {
            ...values,
            dateFrom: values.dateFrom != null ? moment(values.dateFrom).toDate() : null,
            dateTo: values.dateFrom != null ? moment(values.dateTo).toDate() : null,
            pageNr: values.pageNr > 0 ? 0 : values.pageNr
        }

        history.push(location.pathname, { search: request });
    }

    let searchSummaries = async (searchRequest: ISearchRequest) => {
        if (searchRequest.dateFrom == null || searchRequest.dateTo == null) {
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            let response = await axios.post('/api/Ticket/GetTicketSummary', searchRequest);
            setSummaries(response.data.items);
            setTotalRecords(response.data.recordCount);
        } catch (error) { }
        finally {
            setLoading(false);
        }
    }

    let handleChangePage = (event: any, page: number) => {
        let request = {
            ...search,
            dateFrom: moment(search.dateFrom).toDate(),
            dateTo: moment(search.dateTo).toDate(),
            pageNr: page
        }

        window.scrollTo(0, 0);
        history.push(location.pathname, { search: request });
    }

    let updateSearch = (searchRequest: ISearchRequest) => {
        let request: ISearchRequest = {
            ...searchRequest,
            dateFrom: moment(searchRequest.dateFrom),
            dateTo: moment(searchRequest.dateTo)
        }

        setSearch(request);
    }

    let searchCustomers = async (searchText: string, etag: number) => {
        if (!searchText)
            return;

        try {
            const res = await axios.post('/api/Order/CustomersQuickSearch', { searchText, etag });
            return res.data;
        } catch { }
    }

    return (
        <Provider value={{
            recordCount,
            search,
            summaries,
            isLoading,

            doSearch,
            handleChangePage,
            searchSummaries,
            updateSearch,
            searchCustomers
        }}>
            {children}
        </Provider>
    );
};

export default CustomersSummaryProvider;